import { Phone, Mail, MapPin, Facebook, Clock, Send, MessageCircle, CalendarDays, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { businessInfo } from '../data/mock';
import { useState } from 'react';
import { toast } from 'sonner';

const CONTACT_API_URL = process.env.REACT_APP_CONTACT_API_URL || '';

export const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    eventType: '',
    eventDate: '',
    message: ''
  });
  const [sending, setSending] = useState(false);

  const buildMessage = () => {
    const lines = [
      `Hi, I'm ${formData.name}.`,
      formData.phone ? `My phone: ${formData.phone}` : '',
      formData.email ? `My email: ${formData.email}` : '',
      formData.eventType ? `Event type: ${formData.eventType}` : '',
      formData.eventDate ? `Event date: ${formData.eventDate}` : '',
      '',
      formData.message || ''
    ].filter(Boolean);
    return lines.join('\n');
  };

  const handleWhatsApp = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.message) {
      toast.error('Please fill in your name and message.');
      return;
    }
    const msg = encodeURIComponent(buildMessage());
    const url = `https://wa.me/${businessInfo.contact.whatsapp}?text=${msg}`;
    window.open(url, '_blank');
    toast.success('Opening WhatsApp...', {
      description: 'Your enquiry message is ready to send!'
    });
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.message) {
      toast.error('Please fill in your name and message.');
      return;
    }

    if (!CONTACT_API_URL) {
      // Fallback to mailto if API not configured
      const subject = encodeURIComponent(`Sound Hire Enquiry from ${formData.name}`);
      const body = encodeURIComponent(buildMessage());
      window.location.href = `mailto:${businessInfo.contact.email}?subject=${subject}&body=${body}`;
      toast.success('Opening your email client...', {
        description: 'Your enquiry is ready to send!'
      });
      return;
    }

    setSending(true);
    try {
      const res = await fetch(CONTACT_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Failed to send');
      }

      toast.success('Enquiry sent!', {
        description: "We'll get back to you shortly."
      });
      setFormData({ name: '', email: '', phone: '', eventType: '', eventDate: '', message: '' });
    } catch (err) {
      toast.error('Failed to send enquiry', {
        description: err.message || 'Please try WhatsApp instead.'
      });
    } finally {
      setSending(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section id="contact" className="relative py-28 bg-zinc-950">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#50ced4]/20 to-transparent" />

      <div className="max-w-7xl mx-auto px-6">
        {/* Section header */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="text-[#50ced4] text-sm font-medium tracking-widest uppercase mb-4 block">Get In Touch</span>
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-5 leading-tight">
            Ready to Make Your Event{' '}
            <span className="text-[#50ced4]">Sound Amazing?</span>
          </h2>
          <p className="text-zinc-400 text-lg leading-relaxed">
            Contact Mir to discuss your event requirements and get the perfect sound package.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div className="space-y-8">
            {/* Contact cards */}
            <div className="space-y-4">
              {/* Phone */}
              <a
                href={`tel:${businessInfo.contact.phones[0].replace(/\s/g, '')}`}
                className="flex items-center gap-5 p-5 rounded-xl bg-zinc-900/50 border border-white/5 hover:border-[#50ced4]/30 transition-all duration-300 group"
              >
                <div className="w-12 h-12 rounded-xl bg-[#50ced4]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#50ced4]/20 transition-colors">
                  <Phone className="w-5 h-5 text-[#50ced4]" />
                </div>
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Call or Text</p>
                  <p className="text-white font-semibold">{businessInfo.contact.phones[0]}</p>
                  <p className="text-zinc-400 text-sm">{businessInfo.contact.phones[1]}</p>
                </div>
              </a>

              {/* WhatsApp */}
              <a
                href={`https://wa.me/${businessInfo.contact.whatsapp}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-5 p-5 rounded-xl bg-zinc-900/50 border border-white/5 hover:border-[#50ced4]/30 transition-all duration-300 group"
              >
                <div className="w-12 h-12 rounded-xl bg-[#50ced4]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#50ced4]/20 transition-colors">
                  <MessageCircle className="w-5 h-5 text-[#50ced4]" />
                </div>
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">WhatsApp</p>
                  <p className="text-white font-semibold">Message us on WhatsApp</p>
                </div>
              </a>

              {/* Email */}
              <a
                href={`mailto:${businessInfo.contact.email}`}
                className="flex items-center gap-5 p-5 rounded-xl bg-zinc-900/50 border border-white/5 hover:border-[#50ced4]/30 transition-all duration-300 group"
              >
                <div className="w-12 h-12 rounded-xl bg-[#50ced4]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#50ced4]/20 transition-colors">
                  <Mail className="w-5 h-5 text-[#50ced4]" />
                </div>
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Email Us</p>
                  <p className="text-white font-semibold">{businessInfo.contact.email}</p>
                </div>
              </a>

              {/* Facebook */}
              <a
                href={businessInfo.contact.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-5 p-5 rounded-xl bg-zinc-900/50 border border-white/5 hover:border-[#50ced4]/30 transition-all duration-300 group"
              >
                <div className="w-12 h-12 rounded-xl bg-[#50ced4]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#50ced4]/20 transition-colors">
                  <Facebook className="w-5 h-5 text-[#50ced4]" />
                </div>
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Facebook</p>
                  <p className="text-white font-semibold">{businessInfo.contact.facebookHandle}</p>
                </div>
              </a>

              {/* Location */}
              <div className="flex items-center gap-5 p-5 rounded-xl bg-zinc-900/50 border border-white/5">
                <div className="w-12 h-12 rounded-xl bg-[#50ced4]/10 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-[#50ced4]" />
                </div>
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Location</p>
                  <p className="text-white font-semibold">{businessInfo.contact.location}</p>
                </div>
              </div>
            </div>

            {/* Hours */}
            <div className="p-5 rounded-xl bg-zinc-900/30 border border-white/5">
              <div className="flex items-center gap-3 mb-3">
                <Clock className="w-4 h-4 text-[#50ced4]" />
                <span className="text-sm font-medium text-white">Availability</span>
              </div>
              <p className="text-zinc-400 text-sm">Available 7 days a week. For same-day bookings, please call directly.</p>
            </div>
          </div>

          {/* Contact Form */}
          <div className="p-8 rounded-2xl bg-zinc-900/50 border border-white/5 backdrop-blur-sm">
            <h3 className="text-xl font-bold text-white mb-2">Send an Enquiry</h3>
            <p className="text-zinc-500 text-sm mb-6">Fill in your details and send directly via WhatsApp or Email</p>
            <form className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-zinc-500 uppercase tracking-wider mb-2 block">Name *</label>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your name"
                    required
                    className="bg-zinc-800/50 border-zinc-700/50 text-white placeholder:text-zinc-600 focus:border-[#50ced4]/50 rounded-lg"
                  />
                </div>
                <div>
                  <label className="text-xs text-zinc-500 uppercase tracking-wider mb-2 block">Phone</label>
                  <Input
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Your phone number"
                    className="bg-zinc-800/50 border-zinc-700/50 text-white placeholder:text-zinc-600 focus:border-[#50ced4]/50 rounded-lg"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs text-zinc-500 uppercase tracking-wider mb-2 block">Email</label>
                <Input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your@email.com"
                  className="bg-zinc-800/50 border-zinc-700/50 text-white placeholder:text-zinc-600 focus:border-[#50ced4]/50 rounded-lg"
                />
              </div>
              <div>
                <label className="text-xs text-zinc-500 uppercase tracking-wider mb-2 block">Event Type</label>
                <select
                  name="eventType"
                  value={formData.eventType}
                  onChange={handleChange}
                  className="flex h-10 w-full rounded-lg border bg-zinc-800/50 border-zinc-700/50 text-white focus:border-[#50ced4]/50 px-3 py-2 text-sm appearance-none cursor-pointer"
                >
                  <option value="" className="bg-zinc-800">Select event type...</option>
                  <option value="Wedding" className="bg-zinc-800">Wedding</option>
                  <option value="Birthday" className="bg-zinc-800">Birthday</option>
                  <option value="Corporate Event" className="bg-zinc-800">Corporate Event</option>
                  <option value="House Party" className="bg-zinc-800">House Party</option>
                  <option value="Community Event" className="bg-zinc-800">Community Event</option>
                  <option value="School / University" className="bg-zinc-800">School / University</option>
                  <option value="Engagement" className="bg-zinc-800">Engagement</option>
                  <option value="Religious / Cultural" className="bg-zinc-800">Religious / Cultural</option>
                  <option value="Outdoor / Festival" className="bg-zinc-800">Outdoor / Festival</option>
                  <option value="Other" className="bg-zinc-800">Other</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-zinc-500 uppercase tracking-wider mb-2 block">Event Date</label>
                <div className="relative">
                  <CalendarDays className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 pointer-events-none" />
                  <Input
                    name="eventDate"
                    type="date"
                    value={formData.eventDate}
                    onChange={handleChange}
                    min={new Date().toISOString().split('T')[0]}
                    className="bg-zinc-800/50 border-zinc-700/50 text-white placeholder:text-zinc-600 focus:border-[#50ced4]/50 rounded-lg pl-10 [color-scheme:dark]"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs text-zinc-500 uppercase tracking-wider mb-2 block">Message *</label>
                <Textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us about your event and requirements..."
                  rows={4}
                  required
                  className="bg-zinc-800/50 border-zinc-700/50 text-white placeholder:text-zinc-600 focus:border-[#50ced4]/50 rounded-lg resize-none"
                />
              </div>

              {/* Send buttons */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                <Button
                  type="button"
                  onClick={handleWhatsApp}
                  className="w-full bg-[#50ced4] hover:bg-[#3eb8be] text-zinc-950 font-semibold py-5 rounded-xl text-sm"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Send via WhatsApp
                </Button>
                <Button
                  type="button"
                  onClick={handleEmailSubmit}
                  disabled={sending}
                  className="w-full bg-white/5 hover:bg-white/10 text-white font-semibold py-5 rounded-xl text-sm border border-white/10 disabled:opacity-50"
                >
                  {sending ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Mail className="w-4 h-4 mr-2" />
                  )}
                  {sending ? 'Sending...' : 'Send via Email'}
                </Button>
              </div>
              <p className="text-xs text-zinc-600 text-center">
                ⚡ We typically respond within 1 hour during business hours.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};
